import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useResolveEmailDeliveryFailures } from '~/hooks/apis/sailthru/useResolveEmailDeliveryFailures';

import EmailDeliveryFailureResolutionFields from './EmailDeliveryFailureResolutionFields';
import { EMAIL_DELIVERY_FAILURE_RESOLUTION_TYPES, EMAIL_REGEX, EmailDeliveryFailureResolutionType } from './constants';

export interface EmailDeliveryFailureResolutionTarget {
  notificationId: string;
  email?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  targets: Array<EmailDeliveryFailureResolutionTarget>;
  onResolved?: () => void;
}

const DEFAULT_RESOLUTION_TYPE: EmailDeliveryFailureResolutionType = 'IGNORED';

export default function EmailDeliveryFailureResolutionModal({ open, onClose, targets, onResolved }: Props) {
  const [resolutionType, setResolutionType] = useState<EmailDeliveryFailureResolutionType | ''>(
    DEFAULT_RESOLUTION_TYPE,
  );
  const [notes, setNotes] = useState('');
  const [forwardedToEmail, setForwardedToEmail] = useState('');

  const { mutateAsync, isPending } = useResolveEmailDeliveryFailures();

  const isBulkResolution = targets.length > 1;

  useEffect(() => {
    if (!open) {
      return;
    }

    setResolutionType(DEFAULT_RESOLUTION_TYPE);
    setNotes('');
    setForwardedToEmail('');
  }, [open, targets]);

  const isSubmitDisabled = useMemo(() => {
    if (!resolutionType || isPending) {
      return true;
    }

    if (resolutionType === 'FORWARD') {
      return !forwardedToEmail || !EMAIL_REGEX.test(forwardedToEmail);
    }

    return false;
  }, [forwardedToEmail, isPending, resolutionType]);

  const handleClose = useCallback(() => {
    if (isPending) {
      return;
    }

    onClose();
  }, [isPending, onClose]);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!resolutionType || isSubmitDisabled) {
        return;
      }

      const response = await mutateAsync({
        actions: targets.map((target) => ({
          notificationId: target.notificationId,
          resolutionType,
          email: target.email,
          notes: notes.trim() || undefined,
          ...(resolutionType === 'FORWARD' ? { forwardedToEmail } : {}),
        })),
      });

      const allSucceeded = response.result.items.every((item) => item.status === 'SUCCESS');

      if (allSucceeded) {
        onResolved?.();
        onClose();
      }
    },
    [forwardedToEmail, isSubmitDisabled, mutateAsync, notes, onClose, onResolved, resolutionType, targets],
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mark as resolved ({targets.length})</DialogTitle>
      <DialogContent>
        {isBulkResolution && (
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
            This will mark {targets.length} email delivery failures as resolved.
          </Typography>
        )}
        <EmailDeliveryFailureResolutionFields
          resolutionType={resolutionType}
          onResolutionTypeChange={setResolutionType}
          notes={notes}
          onNotesChange={setNotes}
          forwardedToEmail={forwardedToEmail}
          onForwardedToEmailChange={setForwardedToEmail}
          resolutionTypeOptions={[...EMAIL_DELIVERY_FAILURE_RESOLUTION_TYPES]}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text" disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitDisabled}>
          {isPending ? 'Saving...' : 'Mark as resolved'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
